import type { ProductDetailsParams, ProductDetailsResponse } from "@/modules/offers/types/catalog"
import type {
  CreateOfferPayload,
  CreateOfferResponse,
  OfferCreationMetaResponse,
  UploadOfferImagePayload,
  UploadOfferImageResponse,
  UploadOfferReelsRequest,
  UploadOfferReelsResponse
} from "@/modules/offers/types/offerCreationApi"
import type { PaginationParams } from "@/types/api/contracts"
import convertObjectToFormData from "@/utils/convertObjectToFormData"
import { API } from "@/utils/http"

const SELLER_CATALOG_OFFERS_URL = "/v2/seller/catalog/offers"
const SELLER_CATALOG_URL = "/v2/seller/catalog"

type DatesOfOrdersParams = PaginationParams & {
  orderUuid?: string
}

type OrdersByDateParams = PaginationParams & {
  orderUuid?: string
  date?: string
}

export default {
  async get({ pageSize = 10, page = 1, query = {}, signal }: PaginationParams = {}) {
    const paginationQuery = {
      limit: pageSize,
      offset: (page - 1) * pageSize
    }
    const params = {
      ...query,
      ...paginationQuery
    }
    const response = await API.get(SELLER_CATALOG_OFFERS_URL, {
      params,
      signal
    })

    return response
  },

  getOffer(id) {
    return API.get(`${SELLER_CATALOG_OFFERS_URL}/${id}`)
  },

  async getPriceOfferSellers(pCodes) {
    return await API.post(`${SELLER_CATALOG_OFFERS_URL}/products-prices`, pCodes)
  },

  async getMeta() {
    const response = await API.get<OfferCreationMetaResponse>(`${SELLER_CATALOG_OFFERS_URL}/meta`)

    return response
  },

  async getCategories() {
    const response = await API.get(`${SELLER_CATALOG_OFFERS_URL}/meta/categories`)

    return response
  },

  async create(data: CreateOfferPayload) {
    const response = await API.post<CreateOfferResponse>(SELLER_CATALOG_OFFERS_URL, data)

    return response
  },

  async update(uuid, data = {}) {
    const fd = convertObjectToFormData(data)
    const response = await API.put(`${SELLER_CATALOG_OFFERS_URL}/${uuid}`, fd)

    return response
  },

  async addImage(uuid: string, data: UploadOfferImagePayload) {
    const fd = convertObjectToFormData(data)
    return API.post<UploadOfferImageResponse>(`${SELLER_CATALOG_OFFERS_URL}/${uuid}/images`, fd)
  },

  async addReels(uuid: string, data: UploadOfferReelsRequest) {
    const fd = convertObjectToFormData(data)
    const response = await API.post<UploadOfferReelsResponse>(
      `${SELLER_CATALOG_OFFERS_URL}/${uuid}/reels`,
      fd
    )

    return response
  },

  async deleteReels(uuid, filename) {
    const response = await API.delete(`${SELLER_CATALOG_OFFERS_URL}/${uuid}/reels/${filename}`)

    return response
  },

  async updateOffers(payload) {
    const response = await API.put(`${SELLER_CATALOG_OFFERS_URL}/bulk`, payload)

    return response
  },

  async deleteOffer(uuid) {
    return API.delete(`${SELLER_CATALOG_OFFERS_URL}/${uuid}`)
  },

  async deleteOffers(uuids) {
    const response = await API.post(
      `${SELLER_CATALOG_OFFERS_URL}/bulk/delete`,
      uuids.map((uuid) => ({ uuid }))
    )

    return response
  },

  async datesOfOrders({
    pageSize = 10,
    page = 1,
    query = {},
    orderUuid = ""
  }: DatesOfOrdersParams = {}) {
    const paginationQuery = {
      limit: pageSize,
      offset: (page - 1) * pageSize
    }
    const params = {
      ...query,
      ...paginationQuery
    }
    const response = await API.get(`${SELLER_CATALOG_OFFERS_URL}/${orderUuid}/orders/dates`, {
      params
    })

    return response
  },

  async ordersByDate({
    pageSize = 10,
    page = 1,
    query = {},
    orderUuid = "",
    date = ""
  }: OrdersByDateParams = {}) {
    const paginationQuery = {
      limit: pageSize,
      offset: (page - 1) * pageSize
    }
    const params = {
      ...query,
      ...paginationQuery,
      ordered_at: date
    }
    return API.get(`${SELLER_CATALOG_OFFERS_URL}/${orderUuid}/orders`, { params })
  },

  async fetchProductInfo({ code }: ProductDetailsParams) {
    const response = await API.get<ProductDetailsResponse>(
      `${SELLER_CATALOG_URL}/products/${encodeURIComponent(code)}`
    )

    return response
  }
}
